import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@mondivago/supabase';
import { UnifiedSidebar } from '@/components/sidebars/UnifiedSidebar';
import {
  LayoutDashboard,
  Banknote,
  UserCheck,
  Compass,
  CalendarCheck,
  Star,
  Users,
  Tags,
  MapPin,
  BarChart3,
  Share2,
  FileText,
  LifeBuoy,
  MessageSquare,
} from 'lucide-react';

const ADMIN_PATH_MAP: Record<string, string> = {
  admin: 'Admin',
  payouts: 'Payouts',
  suppliers: 'Suppliers',
  partners: 'Partners',
  tours: 'Tours',
  bookings: 'Bookings',
  reviews: 'Reviews',
  travelers: 'Travelers',
  categories: 'Contents',
  locations: 'Locations',
  blogs: 'Blogs',
  legal: 'Legal',
  analytics: 'Analytics',
  tickets: 'Tickets',
  chat: 'Chat',
  settings: 'Settings',
};

const MENU_ITEMS = [
  { path: '/admin', label: 'Admin', icon: LayoutDashboard, end: true },
  { path: '/admin/payouts', label: 'Payouts', icon: Banknote },
  { path: '/admin/suppliers', label: 'Suppliers', icon: UserCheck },
  { path: '/admin/tours', label: 'Tours', icon: Compass },
  { path: '/admin/bookings', label: 'Bookings', icon: CalendarCheck },
  { path: '/admin/reviews', label: 'Reviews', icon: Star },
  { path: '/admin/travelers', label: 'Travelers', icon: Users },
  { path: '/admin/categories', label: 'Contents', icon: Tags },
  { path: '/admin/locations', label: 'Locations', icon: MapPin },
  { path: '/admin/blogs', label: 'Blogs', icon: FileText },
  { path: '/admin/partners', label: 'Partners', icon: Share2 },
  { path: '/admin/legal', label: 'Legal', icon: FileText },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/tickets', label: 'Tickets', icon: LifeBuoy },
  { path: '/admin/chat', label: 'Chat', icon: MessageSquare },
];

interface AdminSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}

export function AdminSidebar({ isCollapsed, setIsCollapsed }: AdminSidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const adminName = useMemo(() => {
    return user?.user_metadata.full_name ?? user?.email?.split('@')[0] ?? 'Super Admin';
  }, [user]);

  const initials = useMemo(() => {
    const parts = adminName.split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'AD';
  }, [adminName]);

  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs = [{ label: 'Admin', to: '/admin' }];

    if (segments.length === 1 && segments[0] === 'admin') {
      return [{ label: 'Admin', to: '/admin' }];
    }

    let currentPath = '';
    segments.forEach((seg) => {
      currentPath += `/${seg}`;
      if (seg === 'admin') return;

      const matchedName = ADMIN_PATH_MAP[seg];
      if (matchedName) {
        crumbs.push({ label: matchedName, to: currentPath });
      } else if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(seg)) {
        crumbs.push({ label: 'Detail', to: currentPath });
      } else {
        crumbs.push({ label: seg.toUpperCase(), to: currentPath });
      }
    });

    return crumbs;
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    void navigate('/', { replace: true });
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-700 font-bold text-xs shadow-sm">
        {initials}
      </div>
    </div>
  );

  return (
    <UnifiedSidebar
      isCollapsed={isCollapsed}
      setIsCollapsed={setIsCollapsed}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      brandLink="/admin"
      menuItems={MENU_ITEMS}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      onSignOut={handleSignOut}
      showHelp={false}
    />
  );
}
